"use client";

import {
  Autocomplete,
  Card,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import useGetAllCategories from "@/hooks/requests/category/use-get-all-categories";
import IProduct from "@/interfaces/product.interface";
import { ProductsMessages } from "@/messages/products/product.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { ProductFilterKey } from "../../types";

type Props = {
  messages: ProductsMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const STATUS_VALUES = {
  TRUE: "true",
  FALSE: "false",
  EMPTY: "",
} as const;

const STATUS_BOOLEAN_VALUES = {
  TRUE: true,
  FALSE: false,
} as const;

function ProductFiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<IProduct>[]>();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { data: categories } = useGetAllCategories();

  const getValueFromProperty = (
    property: string,
    filterValues: IFilter<IProduct>[]
  ): IFilter<IProduct> | undefined => {
    const filter = filterValues.find((f) => f.property === property);
    return filter;
  };

  const handleFilterChange = (
    property: ProductFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const currentFilters = [...prevFilters];

      const existingFilterIndex = currentFilters.findIndex((f) => f.property === property);
      if (existingFilterIndex !== -1) {
        currentFilters.splice(existingFilterIndex, 1);
      }

      const isEmptyValue =
        value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmptyValue) {
        return currentFilters;
      }

      const newFilter: IFilter<IProduct> = {
        property: property as keyof IProduct,
        filterType: filterType,
        value: value,
      };

      return [...currentFilters, newFilter];
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const statusValue =
      selectedValue === STATUS_VALUES.TRUE
        ? STATUS_BOOLEAN_VALUES.TRUE
        : selectedValue === STATUS_VALUES.FALSE
          ? STATUS_BOOLEAN_VALUES.FALSE
          : null;
    handleFilterChange("active", statusValue, FilterType.Equals);
  };

  const handleCategoryChange = (_event: React.SyntheticEvent, value: { id: number; description: string } | null) => {
    const categoryValue = value ? value.id : null;
    handleFilterChange("linkedcategories.categoryid" as ProductFilterKey, categoryValue, FilterType.Equals);
  };

  const handleSkuStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const statusValue =
      selectedValue === STATUS_VALUES.TRUE
        ? STATUS_BOOLEAN_VALUES.TRUE
        : selectedValue === STATUS_VALUES.FALSE
          ? STATUS_BOOLEAN_VALUES.FALSE
          : null;
    handleFilterChange("SKU.Active", statusValue, FilterType.Equals);
  };

  const handleKitChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const kitValue =
      selectedValue === STATUS_VALUES.TRUE
        ? STATUS_BOOLEAN_VALUES.TRUE
        : selectedValue === STATUS_VALUES.FALSE
          ? STATUS_BOOLEAN_VALUES.FALSE
          : null;
    handleFilterChange("SKU.Kit", kitValue, FilterType.Equals);
  };

  const handleHasPhotoChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const hasPhotoValue =
      selectedValue === STATUS_VALUES.TRUE
        ? STATUS_BOOLEAN_VALUES.TRUE
        : selectedValue === STATUS_VALUES.FALSE
          ? STATUS_BOOLEAN_VALUES.FALSE
          : null;
    handleFilterChange("hasProductImages", hasPhotoValue, FilterType.Equals);
  };

  const handleClearFilters = () => {
    setValues([]);
  };

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const renderTextField = (
    name: string,
    label: string,
    property: ProductFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <Grid size={GRID_SIZES.LARGE}>
      <TextField
        name={name}
        label={label}
        onChange={(e) => handleFilterChange(property, e.target.value, filterType)}
        value={getValueFromProperty(property, values)?.value ?? ""}
        error={!!errors[0] && !!errors[0].value}
        helperText={!!errors[0] && errors[0].value}
        fullWidth
        slotProps={{
          inputLabel: { shrink: true },
        }}
      />
    </Grid>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          {renderTextField("code", messages.codeOption, "code")}
          {renderTextField("title", messages.titleOption, "title")}
          {renderTextField("brand", messages.brandOption, "brand")}

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
              <InputLabel shrink>{messages.statusOption}</InputLabel>
              <Select
                value={String(getValueFromProperty("active", values)?.value ?? STATUS_VALUES.EMPTY)}
                onChange={handleStatusChange}
                label={messages.statusOption}
                displayEmpty
                inputProps={{ "aria-label": messages.statusOption }}
              >
                <MenuItem value={STATUS_VALUES.EMPTY}>{messages.selectStatusPlaceholder}</MenuItem>
                <MenuItem value={STATUS_VALUES.TRUE}>{messages.statusTrueOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.FALSE}>{messages.statusFalseOption}</MenuItem>
              </Select>
              {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Collapse in={isExpanded} unmountOnExit>
              <Grid container spacing={2}>
                {renderTextField("skuCode", messages.skuCodeOption, "SKU.Code")}

                <Grid size={GRID_SIZES.LARGE}>
                  <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
                    <InputLabel shrink>{messages.skuStatusOption}</InputLabel>
                    <Select
                      value={String(getValueFromProperty("SKU.Active", values)?.value ?? STATUS_VALUES.EMPTY)}
                      onChange={handleSkuStatusChange}
                      label={messages.skuStatusOption}
                      displayEmpty
                      inputProps={{ "aria-label": messages.skuStatusOption }}
                    >
                      <MenuItem value={STATUS_VALUES.EMPTY}>{messages.selectSkuStatusPlaceholder}</MenuItem>
                      <MenuItem value={STATUS_VALUES.TRUE}>{messages.statusTrueOption}</MenuItem>
                      <MenuItem value={STATUS_VALUES.FALSE}>{messages.statusFalseOption}</MenuItem>
                    </Select>
                    {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={GRID_SIZES.LARGE}>
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(option) => option.description}
                    value={categories.find((cat) => cat.id === getValueFromProperty("linkedcategories.categoryid" as ProductFilterKey, values)?.value) || null}
                    onChange={handleCategoryChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={messages.categoryOption}
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                    noOptionsText="Nenhuma categoria encontrada"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0.25rem !important',
                      },
                    }}
                    fullWidth
                  />
                </Grid>

                {renderTextField("skuEan", messages.skuEanOption, "SKU.Ean")}

                <Grid size={GRID_SIZES.LARGE}>
                  <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
                    <InputLabel shrink>{messages.kitOption}</InputLabel>
                    <Select
                      value={String(getValueFromProperty("SKU.Kit", values)?.value ?? STATUS_VALUES.EMPTY)}
                      onChange={handleKitChange}
                      label={messages.kitOption}
                      displayEmpty
                      inputProps={{ "aria-label": messages.kitOption }}
                    >
                      <MenuItem value={STATUS_VALUES.EMPTY}>{messages.selectKitPlaceholder}</MenuItem>
                      <MenuItem value={STATUS_VALUES.TRUE}>{messages.kitTrueOption}</MenuItem>
                      <MenuItem value={STATUS_VALUES.FALSE}>{messages.kitFalseOption}</MenuItem>
                    </Select>
                    {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={GRID_SIZES.LARGE}>
                  <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
                    <InputLabel shrink>{messages.hasPhotoOption}</InputLabel>
                    <Select
                      value={String(getValueFromProperty("hasProductImages", values)?.value ?? STATUS_VALUES.EMPTY)}
                      onChange={handleHasPhotoChange}
                      label={messages.hasPhotoOption}
                      displayEmpty
                      inputProps={{ "aria-label": messages.hasPhotoOption }}
                    >
                      <MenuItem value={STATUS_VALUES.EMPTY}>{messages.selectHasPhotoPlaceholder}</MenuItem>
                      <MenuItem value={STATUS_VALUES.TRUE}>{messages.hasPhotoTrueOption}</MenuItem>
                      <MenuItem value={STATUS_VALUES.FALSE}>{messages.hasPhotoFalseOption}</MenuItem>
                    </Select>
                    {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
                  </FormControl>
                </Grid>

                {renderTextField(
                  "shortDescription",
                  messages.shortDescriptionOption,
                  "shortDescription"
                )}
                {renderTextField("description", messages.descriptionOption, "description")}
                {renderTextField("ncm", messages.ncmOption, "ncm")}
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={isExpanded ? 2 : 0}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="outlined" color="dark" onClick={handleToggleExpanded} fullWidth>
                  {isExpanded ? messages.lessFiltersButton : messages.moreFiltersButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default ProductFiltersForm;
